// Auth context and hook with Neon Auth integration
// Reference: https://neon.com/docs/auth/quick-start/react

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authClient } from '../lib/auth';
import { authService } from '../services/auth';
import type { User } from '@propman/shared';

interface AuthContextType {
  user: User | null;
  organization: { id: string; name: string; logoUrl: string | null } | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<{ id: string; name: string; logoUrl: string | null } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      setError(null);

      // Check if there's an active Neon Auth session
      const session = await authClient.getSession();

      if (!session?.data?.session) {
        setUser(null);
        setOrganization(null);
        setIsLoading(false);
        return;
      }

      // We have a session, fetch user details from our API
      const response = await authService.fetchCurrentUser();
      setUser(response.user);
      setOrganization(response.organization);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setError('Failed to load user');

      // Clear state on auth error
      if (err instanceof Error && 'status' in err && (err as { status: number }).status === 401) {
        await authService.logout();
        setUser(null);
        setOrganization(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Update page title with organization name for white-labeling
  useEffect(() => {
    if (organization?.name) {
      document.title = `${organization.name} | Property Portal`;
    } else {
      document.title = 'Property Management Portal';
    }
  }, [organization?.name]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.login(email, password);
      await refreshUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.signUp(email, password, name);
      await refreshUser();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setOrganization(null);
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    organization,
    isLoading,
    isAuthenticated: user !== null,
    error,
    login,
    signUp,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;
